import Vue from 'vue'
import VueRouter from 'vue-router'
import LoginView from '../views/LoginView.vue'
import Layout from '@/components/layout/Layout.vue'
import StoreSelectView from '@/views/setup/StoreSelectView.vue'
import UserLoginView from '@/views/setup/UserLoginView.vue'
import store from "@/store";

Vue.use(VueRouter)

/**
 * Vue Router definition for the Supervisor App.
 *
 * Routes:
 *   /login                 - Boot/connection test
 *   /select-store          - Pick which store to monitor
 *   / (UserLoginView)      - PIN auth (matches emp.Password)
 *   /view-requests         - Live pending-request list (the main work surface)
 *   /approve-request       - Deep-link target. Accepts ?id={requestId} (required)
 *                            and ?storeDB={DB} (informational). When the OS hands
 *                            a lytsoft-supervisor://approveRequest?storeDB=...&id=...
 *                            URL to this app, Capacitor 3 puts it in window.location;
 *                            the WebView loads the SPA, Vue Router matches this
 *                            route, and the guard below redirects to /view-requests
 *                            with the same ?id query param preserved. RequestsView
 *                            then reads ?id on mount and pre-opens the detail modal
 *                            for the matching row.
 *
 * The route is intentionally a thin redirect: it does NOT need a separate view,
 * because the deep-link user experience (per Felix 2026-06-09) is the same screen
 * the supervisor already uses - just with the relevant row pre-selected.
 */
const routes = [
    {
        path: '/login',
        name: 'login',
        component: LoginView
    },
    {
        path: '/select-store',
        name: 'select-store',
        component: StoreSelectView
    },
    {
        path: '/',
        name: 'user-login',
        component: UserLoginView
    },
    {
        path: '/',
        component: Layout,
        children: [
            {
                path: '/view-requests',
                name: 'view-requests',
                component: () => import(/*webpackPrefetch: true  ,  webpackChunkName: "view-requests" */ '@/views/Overrides/RequestsView'),
            },
            {
                /**
                 * Deep-link target. Resolves to /view-requests with the same
                 * ?id= query param so RequestsView's on-mount logic opens the
                 * detail modal for that pending request.
                 *
                 * ?storeDB is informational - the app already knows the store
                 * from Storage.getCurrentStoreDB() because the supervisor
                 * selected it during PIN login. It is accepted for symmetry
                 * with the BackOffice 302 redirect.
                 */
                path: '/approve-request',
                name: 'approve-request',
                redirect: to => {
                    // Preserve ?id (and ?storeDB if present) so RequestsView
                    // can pick the matching row out of the WebSocket-pushed
                    // Vuex getPendingRequests list.
                    const query = {...to.query};
                    return {name: 'view-requests', query};
                }
            }
        ]
    }
]

const router = new VueRouter({
    mode: 'history',
    base: process.env.BASE_URL,
    routes
})

/**
 * Global navigation guard.
 *
 * Ensures the WebSocket is connected before any view renders, so the
 * pending-request list (Vuex getPendingRequests) is being pushed by the
 * /topic/pendingRequests/{storeDB} subscription. RequestsView's deep-link
 * path depends on the request already being in that list.
 */
router.beforeEach(async (to, from , next)=>{
    if (!store.getters.isConnected) {
        store.dispatch("connectWebSocket").catch((error) => {
            console.error("WebSocket connection failed:", error);
        });
    }
    next();
});
export default router
